import { Avatar } from 'components/Avatar';
import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ItemDetails } from 'components/ItemDetails';
import { Logo } from 'components/Logo';
import { navigateScreenAware } from 'components/Navigation/navigationRef';
import { ModalScreens, Screens, Stacks } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { UserAvatar } from 'components/UserAvatar';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { APP_VERSION } from 'lib/getDeviceInformation';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  CHECK_FOR_APP_UPDATE_MANUAL,
  NAVIGATE_MODAL,
  doSetupAseLoad,
  REMOTE_ASESETUP_LOAD,
  doFieldSettingsLoad,
  doSettingOrganisationSelect,
  doSetupBillingOpen,
  doSettingsAseChecksLoad,
  doSetupApiOpen,
  doSetupEngageOpen,
  doSetupBillingAdminOpen,
  doSetupSsoOpen,
  eventSelectScreenSelection,
  doSettingOrganisationSelectStart,
  doSetupOrganisationOpen,
} from 'store/actions';
import { selectAppUpdateIsAvailable } from 'store/app/selectors';
import {
  selectUserEmail,
  selectUserName,
  selectUserSelectedOrganisation,
} from 'store/auth/selectors';

export const SettingsExplorerScreen: React.FC = () => {
  const dispatch = useDispatch();

  const eventKeyChangeOrganisation = 'settingsExplorerChooseOrganisation';

  const appStoreUpdateIsAvailable = useSelector(selectAppUpdateIsAvailable);
  const userName = useSelector(selectUserName)!;
  const userEmail = useSelector(selectUserEmail)!;

  const organisation = useSelector(selectUserSelectedOrganisation);

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeyChangeOrganisation) {
          const newOrganisationId = event.payload.stringValues[0];
          if (newOrganisationId) {
            dispatch(
              doSettingOrganisationSelect({
                id: newOrganisationId,
              }),
            );
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch]);

  return (
    <PageScrollView
      isInModal={false}
      header={<NavigationHeader heading="Settings" />}
    >
      <Card>
        <ViewWithSeparators
          separatorColor="transparent"
          separatorMargin={theme.paddingHorizontal / 8}
        >
          <CardRow
            onPress={() =>
              navigateScreenAware(
                Stacks.SETTINGS,
                Screens.SETTINGS_PROFILE,
                undefined,
              )
            }
            navigationIsSelected={{
              name: Screens.SETTINGS_PROFILE,
              params: {},
            }}
            childrenStyle={{
              alignItems: 'center',
              flex: 1,
              flexDirection: 'row',
              justifyContent: 'center',
            }}
          >
            <UserAvatar
              size={70}
              style={{ marginRight: theme.paddingHorizontal }}
              previewOnPress={false}
            />
            <ItemDetails title={userName} subtitle={userEmail} />
          </CardRow>
          {Platform.OS !== 'web' && (
            <CardRow
              label="App Settings"
              onPress={() =>
                navigateScreenAware(
                  Stacks.SETTINGS,
                  Screens.SettingsGeneral,
                  undefined,
                )
              }
              icon={ICONS.SlidersUpLight}
              iconColor="white"
              navigationIsSelected={{
                name: Screens.SettingsGeneral,
                params: {},
              }}
              iconPressableStyle={{
                backgroundColor: theme.colors.blue,
                marginLeft: 0,
                marginRight: theme.paddingHorizontal,
              }}
            />
          )}
        </ViewWithSeparators>
      </Card>

      {organisation && organisation.address ? (
        <>
          <Card
            label="Organisation"
            labelLinkText="Switch"
            onLabelLinkPress={() =>
              dispatch(doSettingOrganisationSelectStart())
            }
            margin={false}
          />
          <Card>
            <ViewWithSeparators
              separatorColor="transparent"
              separatorMargin={theme.paddingHorizontal / 8}
            >
              <CardRow
                onPress={() =>
                  dispatch(doSetupOrganisationOpen({ newOrganisationId: null }))
                }
                navigationIsSelected={{
                  name: Screens.SETTINGS_ORGANISATION,
                  params: {},
                }}
                childrenStyle={{
                  alignItems: 'center',
                  flex: 1,
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                <Avatar
                  icon={ICONS.HouseBuildingLight}
                  svgData={organisation.svgLogo || undefined}
                  imageData={organisation.pngLogo || undefined}
                  size={70}
                  imagePadding={7}
                  svgPadding={7}
                  iconPadding={12}
                  style={{ marginRight: theme.paddingHorizontal }}
                  imageBackgroundColor={
                    organisation.svgLogo || organisation.pngLogo
                      ? theme.colors.background
                      : undefined
                  }
                  resizeMode="contain"
                />
                <ItemDetails
                  title={organisation.address.name}
                  subtitle={[
                    organisation.address.address1,
                    organisation.address.address2,
                    organisation.address.city,
                    organisation.address.county,
                    organisation.address.postcode,
                  ]
                    .filter(filterArrayEmpty)
                    .join(', ')}
                />
              </CardRow>
              {organisation.userFeatures.billingSupport && (
                <CardRow
                  label="Billing Admin"
                  icon={ICONS.SterlingSignLight}
                  iconColor="white"
                  onPress={() =>
                    dispatch(
                      doSetupBillingAdminOpen({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SettingsBillingAdmin,
                    params: {},
                  }}
                  iconPressableStyle={{
                    backgroundColor: '#FE9500',
                    marginLeft: 0,
                    marginRight: theme.paddingHorizontal,
                  }}
                  rbs
                />
              )}
              {organisation.userFeatures.billing && (
                <CardRow
                  label="Invoices"
                  icon={ICONS.ReceiptLight}
                  iconColor="white"
                  onPress={() =>
                    dispatch(
                      doSetupBillingOpen({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SETTINGS_BILLING,
                    params: {},
                  }}
                  iconPressableStyle={{
                    backgroundColor: '#FE9500',
                    marginLeft: 0,
                    marginRight: theme.paddingHorizontal,
                  }}
                />
              )}
              {(organisation.userFeatures.api ||
                organisation.userFeatures.sso ||
                organisation.userFeatures.engage) && (
                <ViewWithSeparators>
                  {organisation.userFeatures.api && (
                    <CardRow
                      label="API & Webhooks"
                      icon={ICONS.GearComplexCodeLight}
                      iconColor="white"
                      onPress={() =>
                        dispatch(
                          doSetupApiOpen({
                            organisationId: organisation.id,
                          }),
                        )
                      }
                      navigationIsSelected={{
                        name: Screens.SettingsApi,
                        params: {},
                      }}
                      iconPressableStyle={{
                        backgroundColor: '#007AFF',
                        marginLeft: 0,
                        marginRight: theme.paddingHorizontal,
                      }}
                    />
                  )}
                  {organisation.userFeatures.sso && (
                    <CardRow
                      label="SSO & Provisioning"
                      icon={ICONS.PersonWalkingDashedLineArrowRightLight}
                      iconColor="white"
                      onPress={() =>
                        dispatch(
                          doSetupSsoOpen({
                            organisationId: organisation.id,
                          }),
                        )
                      }
                      navigationIsSelected={{
                        name: Screens.SettingsSso,
                        params: {},
                      }}
                      iconPressableStyle={{
                        backgroundColor: '#007AFF',
                        marginLeft: 0,
                        marginRight: theme.paddingHorizontal,
                      }}
                    />
                  )}
                  {organisation.userFeatures.engage && (
                    <CardRow
                      label="Engage"
                      icon={ICONS.PersonLight}
                      iconColor="white"
                      onPress={() =>
                        dispatch(
                          doSetupEngageOpen({
                            organisationId: organisation.id,
                          }),
                        )
                      }
                      navigationIsSelected={{
                        name: Screens.SettingsEngage,
                        params: {},
                      }}
                      iconPressableStyle={{
                        backgroundColor: '#007AFF',
                        marginLeft: 0,
                        marginRight: theme.paddingHorizontal,
                      }}
                    />
                  )}
                </ViewWithSeparators>
              )}
              {organisation.userFeatures.fields && (
                <ViewWithSeparators>
                  <CardRow
                    label="Fields"
                    icon={ICONS.InputTextDuo}
                    onPress={() =>
                      dispatch(
                        doFieldSettingsLoad.request({
                          id: organisation.id,
                        }),
                      )
                    }
                    navigationIsSelected={{
                      name: Screens.SETTINGS_FIELDS,
                      params: {},
                    }}
                    iconColor="white"
                    iconPressableStyle={{
                      backgroundColor: '#5756D5',
                      marginLeft: 0,
                      marginRight: theme.paddingHorizontal,
                    }}
                  />
                </ViewWithSeparators>
              )}
              {organisation.userFeatures.assessments && (
                <ViewWithSeparators>
                  {organisation.userFeatures.elementChecks && (
                    <CardRow
                      label="Element Checks"
                      icon={ICONS.ListCheckLight}
                      iconColor="white"
                      iconPressableStyle={{
                        backgroundColor: '#35C759',
                        marginLeft: 0,
                        marginRight: theme.paddingHorizontal,
                      }}
                      onPress={() =>
                        dispatch(
                          doSettingsAseChecksLoad({
                            organisationId: organisation.id,
                          }),
                        )
                      }
                      navigationIsSelected={{
                        name: Screens.SettingsAssessmentCompliance,
                        params: {},
                      }}
                      rbs
                    />
                  )}
                  <CardRow
                    label="Checklists & Questions"
                    icon={ICONS.CommentCheckLight}
                    iconColor="white"
                    iconPressableStyle={{
                      backgroundColor: '#35C759',
                      marginLeft: 0,
                      marginRight: theme.paddingHorizontal,
                    }}
                    onPress={() =>
                      dispatch(
                        REMOTE_ASESETUP_LOAD({
                          organisationId: organisation.id,
                        }),
                      )
                    }
                    navigationIsSelected={{
                      name: Screens.SETTINGS_QUESTIONS,
                      params: {},
                    }}
                  />
                  <CardRow
                    icon={ICONS.BallotCheckLight}
                    label="Assessment Templates"
                    iconColor="white"
                    iconPressableStyle={{
                      backgroundColor: '#35C759',
                      marginLeft: 0,
                      marginRight: theme.paddingHorizontal,
                    }}
                    onPress={() =>
                      dispatch(
                        doSetupAseLoad({
                          organisationId: organisation.id,
                        }),
                      )
                    }
                    navigationIsSelected={{
                      name: Screens.SETTINGS_ASSESSMENTS,
                      params: {},
                    }}
                  />
                </ViewWithSeparators>
              )}
            </ViewWithSeparators>
          </Card>
        </>
      ) : (
        <Card label="Organisation">
          <CardRow
            label="Select an organisation..."
            onPress={() => dispatch(doSettingOrganisationSelectStart())}
            icon={ICONS.HouseBuildingLight}
          />
        </Card>
      )}

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-evenly',
        }}
      >
        <Logo width={80} />
        <Button
          text={`Version ${APP_VERSION}`}
          onPress={
            Platform.OS === 'web'
              ? undefined
              : () => dispatch(CHECK_FOR_APP_UPDATE_MANUAL())
          }
          buttonTheme={ButtonTheme.themeless}
          textSize="xxxsmall"
          style={{
            alignSelf: 'center',
          }}
          destructive={appStoreUpdateIsAvailable !== false}
        />

        <Button
          text="Support"
          icon={ICONS.LifeRingLight}
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({ screen: ModalScreens.Support, params: {} }),
            )
          }
          buttonTheme={ButtonTheme.themeless}
          textSize="xxxsmall"
          style={{
            alignSelf: 'center',
          }}
        />
      </View>
    </PageScrollView>
  );
};
